import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { useCallback, useContext, useLayoutEffect, useMemo } from 'react';
import {
    AccessoriesPageData,
} from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { useUserInputState } from 'src/modules/user-input/useUserInputState.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { sortAccessoriesBy } from 'src/modules/common/utils/sortAccessories.ts';
import { Filter } from 'src/utils/composeFilter.ts';
import { AccessoryGrid } from 'src/common/Accessory/AccessoryGrid';
import { Accessory } from 'src/common/Accessory/Accessory';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { LocationState } from 'src/modules/LocationState.ts';
import { GetQty, parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { useGetHomeBreadcrumb } from 'src/modules/home/useGetHomeBreadcrumb.ts';
import { isGatewayAccessory, isRegularAccessory, isToolsAccessory } from 'src/modules/common/AccessoryFilters.ts';
import { ValveBreadcrumbs } from 'src/modules/common/components/ValveBreadcrumbs/ValveBreadcrumbs';
import { PATHS } from 'src/types/Paths';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';
import { AccessoriesPipeConnectors } from 'src/common/AccessoriesPipeConnectors/AccessoriesPipeConnectors.tsx';
import { excludeIncompatible } from 'src/modules/accessories/excludeIncompatible.ts';
import { useNavigate } from 'react-router-dom';
import { NoData } from 'src/modules/common/components/NoData/NoData.tsx';
import { isNotFromBreadCrumb } from 'src/common/breadcrumbUtils.ts';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { Price } from 'src/common/Price/Price.tsx';
import { ConfigContext } from 'src/configContext.ts';
import { RegionsEnum } from 'src/generated/operations.ts';

export const AccessoriesPage = ({
    data,
    filter,
}: {
    data: AccessoriesPageData;
    filter: Filter;
}) => {
    const { isMobile, isRevitPlugin } = useContext(RuntimeContext);
    const { userSettings, currency } = useContext(ConfigContext);
    const navigate = useNavigate();

    const { state: userInputState, searchParams, locationState, setLocationState } = useUserInputState([]);

    const onChange = useCallback((label: string, getQty: GetQty) => {
        let selectedAccessories: LocationState['selectedAccessories'] = [];
        if (locationState.selectedAccessories.find((item) => item.label === label) !== undefined) {
            selectedAccessories = locationState.selectedAccessories.filter((item) => item.label !== label);
        } else {
            selectedAccessories = [...locationState.selectedAccessories, { label, qty: getQty(label) }];
        }
        setLocationState({ ...locationState, selectedAccessories });
    }, [locationState, setLocationState]);

    const {
        isRecommended,
        getQty,
    } = parseRecommended(data.vssAccessories.valve.connectors);

    const onPipeConnectorsChange = useCallback((value: boolean) => {
        setLocationState({
            ...locationState,
            pipeConnectors: value,
            selectedAccessories: [
                ...locationState.selectedAccessories.filter(({ label }) => !isRecommended(label)),
                ...(value ? data.vssAccessories.valveAccessories
                    .filter(({ item }) => isRecommended(item.Label))
                    .map(({ item }) => ({ label: item.Label, qty: getQty(item.Label) }))
                    : []),
            ],
        });
    }, [setLocationState, locationState, data, isRecommended, getQty]);

    const onPipeConnectorSelect = useCallback((label: string) => {
        let selectedAccessories: LocationState['selectedAccessories'] = [];
        if (locationState.selectedAccessories.find((item) => item.label === label) !== undefined) {
            selectedAccessories = locationState.selectedAccessories.filter((item) => item.label !== label);
        } else {
            selectedAccessories = [...locationState.selectedAccessories, { label, qty: getQty(label) }];
        }
        setLocationState({ ...locationState, pipeConnectors: false, selectedAccessories });
    }, [getQty, locationState, setLocationState]);

    const homePageValue = useGetHomeBreadcrumb({ filter });

    const resetBreadcrumb = useCallback(() => {
        setLocationState({
            ...locationState,
            selectedAccessories: [],
            pipeConnectors: false,
        });
    }, [locationState, setLocationState]);

    const nextLocationState = useMemo(() => ({
        ...locationState,
        selectedAccessories: [
            ...locationState.selectedAccessories.filter(({ label }) => !isRecommended(label)),
            ...(locationState.pipeConnectors ? data.vssAccessories.valveAccessories
                .filter(({ item }) => isRecommended(item.Label))
                .map(({ item }) => ({ label: item.Label, qty: getQty(item.Label) }))
                : []),
        ],
    }), [locationState, data.vssAccessories.valveAccessories, isRecommended, getQty]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="forward"
                eventData={{
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 5 - add to schedule',
                    eventLabel: locationState.selectedAccessories.length > 0
                        ? 'add combo and accessories to schedule' : 'add to schedule',
                }}
                state={nextLocationState}
                to={{
                    pathname: PATHS.Checkout,
                    search: searchParams.toString(),
                }}
                isMobile={isMobile}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const renderAccessory = (
        item: AccessoriesPageData['vssAccessories']['valveAccessories'][0]['item'],
    ) => {
        const price = getPriceValue(
            item,
            userSettings.shoppingCenter as keyof typeof item,
            currency,
        );
        return (
            <Accessory
                chip={isRecommended(item.Label) ? <I18n messageId={MESSAGES.recommended}/> : null}
                accessory={item}
                onSelect={() => onChange(item.Label, getQty)}
                selected={locationState.selectedAccessories.find(({ label }) => label === item.Label) !== undefined}
                key={`${item.Label}-${item.ID}`}
                price={userSettings.currentRegion !== RegionsEnum.Am ? (
                    <Price
                        price={price.value}
                        currency={price.currency}
                        locale={userSettings.currentLocale}
                    />
                ) : null}
            />
        );
    };

    const incompatibleFilter = excludeIncompatible([locationState.valveLabel, locationState.actuatorLabel]);

    const [pipeConnectors, valveAccessories, actuatorAccessories] = useMemo(() => [
        data.vssAccessories.valveAccessories
            .filter(({ item }) => incompatibleFilter(item) && isRecommended(item.Label))
            .sort(sortAccessoriesBy(isRecommended)),
        data.vssAccessories.valveAccessories
            .filter(({ item }) => incompatibleFilter(item) && !isRecommended(item.Label)),
        data.vssAccessories.actuatorAccessories
            ?.filter(({ item }) => incompatibleFilter(item) && !isRecommended(item.Label)) || [],
    ], [data, incompatibleFilter, isRecommended]);

    const isValveActuatorUnit = locationState.valveLabel === locationState.actuatorLabel;

    useLayoutEffect(() => {
        const noAccessories = pipeConnectors.length + valveAccessories.length +
            (isValveActuatorUnit ? 0 : actuatorAccessories.length) === 0;
        if (isNotFromBreadCrumb(searchParams) && noAccessories) {
            navigate({
                pathname: PATHS.Checkout,
                search: searchParams.toString(),
            }, {
                state: nextLocationState,
            });
        }
    }, [
        navigate,
        pipeConnectors,
        valveAccessories,
        isValveActuatorUnit,
        actuatorAccessories,
        searchParams,
        nextLocationState,
    ]);

    return (
        <>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.accessoriesPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <ValveBreadcrumbs
                            currentPage={PATHS.Accessories}
                            homeValue={homePageValue}
                            userInputState={userInputState}
                            locationState={locationState}
                            searchParams={searchParams}
                            resetHandler={locationState.selectedAccessories.length > 0 ? resetBreadcrumb : undefined}
                        />
                    )}
                    selectionButton={!isRevitPlugin && (
                        <SelectionsButton
                            isMobile={isMobile}
                            searchParams={searchParams}
                            locationState={locationState}
                        />
                    )}
                />
                {pipeConnectors.length + valveAccessories.length +
                    actuatorAccessories.length === 0 && (<NoData/>)}
                <div className="vss-accessories-page">
                    {pipeConnectors.length > 0 && (
                        <AccessoriesPipeConnectors
                            isMobile={isMobile}
                            isSelected={locationState.pipeConnectors}
                            selectedAccessories={locationState.selectedAccessories}
                            pipeConnectors={pipeConnectors}
                            onSelect={onPipeConnectorSelect}
                            onPipeConnectorsChange={onPipeConnectorsChange}
                            getQty={getQty}
                            isPricesAllowed={userSettings.currentRegion !== RegionsEnum.Am}
                        />
                    )}
                    {valveAccessories && valveAccessories.filter(isRegularAccessory).length > 0 && (
                        <AccessoryGrid
                            title={MESSAGES.accessoriesPageValvesTitle}
                            isMobile={isMobile}
                        >
                            {valveAccessories.filter(isRegularAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {valveAccessories && valveAccessories.filter(isGatewayAccessory).length > 0 && (
                        <AccessoryGrid
                            title={MESSAGES.accessoriesPageGateways}
                            isMobile={isMobile}
                        >
                            {valveAccessories.filter(isGatewayAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {valveAccessories && valveAccessories.filter(isToolsAccessory).length > 0 && (
                        <AccessoryGrid
                            title={MESSAGES.accessoriesPageTools}
                            isMobile={isMobile}
                        >
                            {valveAccessories.filter(isToolsAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {!isValveActuatorUnit &&
                        actuatorAccessories.filter(isRegularAccessory).length > 0 && (
                        <AccessoryGrid
                            title={MESSAGES.accessoriesPageActuatorTitle}
                            isMobile={isMobile}
                        >
                            {actuatorAccessories.filter(isRegularAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {!isValveActuatorUnit &&
                        actuatorAccessories.filter(isGatewayAccessory).length > 0 && (
                        <AccessoryGrid
                            title={MESSAGES.accessoriesPageGateways}
                            isMobile={isMobile}
                        >
                            {actuatorAccessories.filter(isGatewayAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}

                    {!isValveActuatorUnit &&
                        actuatorAccessories.filter(isToolsAccessory).length > 0 && (
                        <AccessoryGrid
                            title={MESSAGES.accessoriesPageTools}
                            isMobile={isMobile}
                        >
                            {actuatorAccessories.filter(isToolsAccessory)
                                .sort(sortAccessoriesBy(isRecommended))
                                .map(
                                    ({ item }) => renderAccessory(item),
                                )}
                        </AccessoryGrid>
                    )}
                </div>

            </MainLayout>

            {isMobile && navigation}
        </>
    );
};
